import React from 'react';
import { useSelector } from 'react-redux';
import { history } from '@carvana/purchase-store';
import styled, { css } from 'styled-components';
import {
  ArrowForward,
  CheckCircle,
  CheckCircleOutlineUnchecked
} from '@carvana/icons';
import { DashboardProgressActive } from '@carvana/purchase-icons';

type NavItem = {
  inProgress?: boolean;
  isComplete?: boolean;
  isDisabled?: boolean;
  route?: string;
  title?: string;
  completionTimeEstimate?: number;
};

type UiState = {
  ui: {
    showSummary: boolean;
  };
};

const navSectionStyles = css`
  align-items: center;
  border-bottom: 1px solid ${props => props.theme.carvana.gray.light};
  display: flex;
  padding: 0 30px;
`;

const NavTab = styled.div`
  ${navSectionStyles}
  cursor: pointer;
  height: 86px;

  &.disabled {
    color: ${props => props.theme.carvana.gray.light};
    cursor: not-allowed;
  }

  &.active {
    background-color: ${props => props.theme.carvana.gray.background};
  }
`;

const common = {
  navIconSize: {
    height: '18px',
    width: '18px'
  },
  navTabText: css`
    ${NavTab}.disabled & {
      color: ${props => props.theme.carvana.gray.medium};
    }
  `
};

const NavTabIconWrapper = styled.div`
  margin-right: 18px;
`;

const CompleteIconGreen = styled(CheckCircle)`
  & > svg {
    ${common.navIconSize};
  }

  & > svg > g {
    fill: ${props => props.theme.carvana.green.primary};
  }
`;

const IncompleteIcon = styled(CheckCircleOutlineUnchecked)`
  & > svg {
    ${common.navIconSize};
  }
`;

const NavTabTextWrapper = styled.div``;

const NavTabText = styled.p`
  ${common.navTabText}
  color: ${props => props.theme.carvana.blue.dark};
  font-weight: 500;
`;

const NavTabSubtext = styled.p`
  ${common.navTabText}
  color: ${props => props.theme.carvana.gray.dark};
  font-size: 14px;
  margin-top: 8px;
`;

const NavTabArrow = styled(ArrowForward)`
  margin-left: auto;

  & > svg {
    height: 14px;
    width: 10px;
  }

  & > svg > g {
    fill: ${props => props.theme.carvana.gray.medium};

    ${NavTab}.active & {
      fill: ${props => props.theme.carvana.blue.dark};
    }
  }
`;

const defaultProps = {
  isActive: false,
  navItem: {} as NavItem,
  setIsNavOpen: (_isOpen: boolean) => {},
  menuRef: React.createRef<HTMLDivElement>()
};

export const NavigationTab = ({
  isActive,
  navItem,
  setIsNavOpen,
  menuRef
} = defaultProps) => {
  const { showSummary } = useSelector((state: UiState) => state.ui);
  const { isComplete, isDisabled, route } = navItem;
  const timeEst = navItem?.completionTimeEstimate;

  return (
    <NavTab
      className={[
        isDisabled && 'disabled',
        isActive && !showSummary && 'active'
      ].join(' ')}
      onClick={() => {
        if (!isDisabled) {
          setIsNavOpen(false);
          if (menuRef.current) {
            menuRef.current.scrollTop = 0;
          }
        }
        !isDisabled && (!isActive || showSummary) && history.push(route || '');
      }}
    >
      <NavTabIconWrapper>
        {navItem.inProgress ? (
          <DashboardProgressActive />
        ) : isComplete ? (
          <CompleteIconGreen />
        ) : (
          <IncompleteIcon />
        )}
      </NavTabIconWrapper>
      <NavTabTextWrapper>
        <NavTabText>{navItem.title}</NavTabText>
        {timeEst && (
          <NavTabSubtext>
            {isComplete
              ? 'Complete'
              : `About ${timeEst} minute${timeEst > 1 ? 's' : ''}`}
          </NavTabSubtext>
        )}
      </NavTabTextWrapper>
      {(!isDisabled || showSummary) && <NavTabArrow />}
    </NavTab>
  );
};

export default NavigationTab;
